import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { permissionService } from '../services/permissionService';
import { getFetchErrorMessage } from '../services/apiService';

interface UserRole {
  userId: string;
  userName: string;
  userEmail?: string;
  roleId: string;
  roleName: string;
  roleDescription?: string;
}

interface UserRolesTableProps {
  externalAddTrigger?: number;
  hideAddButton?: boolean;
  onAddClick?: () => void;
  deletePermission?: string;
  refreshTrigger?: number;
}

const UserRolesTable: React.FC<UserRolesTableProps> = ({ 
  externalAddTrigger, 
  hideAddButton = false,
  onAddClick,
  deletePermission,
  refreshTrigger 
}) => {
  const [, forceUpdate] = useState({});
  const canDelete = deletePermission ? permissionService.hasPermission(deletePermission) : true;
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUserRole, setSelectedUserRole] = useState<UserRole | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Subscribe to permission changes
  useEffect(() => {
    const unsubscribe = permissionService.onPermissionsChanged(() => {
      forceUpdate({});
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    fetchUserRoles();
  }, []);

  // Refresh when external trigger changes
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      fetchUserRoles();
    }
  }, [refreshTrigger]);

  // Handle external add trigger
  useEffect(() => {
    if (externalAddTrigger && externalAddTrigger > 0 && onAddClick) {
      onAddClick();
    }
  }, [externalAddTrigger, onAddClick]);

  const fetchUserRoles = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/userroles`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUserRoles(data);
      } else {
        setErrorMessage(await getFetchErrorMessage(response, 'Nem sikerült betölteni a felhasználói szerepeket.'));
      }
    } catch (error) {
      console.error('Error fetching user roles:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Nem sikerült betölteni a felhasználói szerepeket.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (userRole: UserRole) => {
    setSelectedUserRole(userRole);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUserRole) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/userroles`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          userId: selectedUserRole.userId, 
          roleId: selectedUserRole.roleId 
        }),
      });

      if (response.ok) {
        setSuccessMessage('Felhasználói szerepkör sikeresen eltávolítva');
        fetchUserRoles();
      } else {
        throw new Error(await getFetchErrorMessage(response, 'Hiba történt a szerepkör eltávolítása során.'));
      }
    } catch (error) {
      console.error('Error removing user role:', error);
      setErrorMessage('Hiba történt a szerepkör eltávolítása során');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedUserRole(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Felhasználói szerepek betöltése...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Typography variant="h4" gutterBottom>
          Felhasználói szerepek kezelése
        </Typography>
        {!hideAddButton && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddClick}
            sx={{ backgroundColor: '#ff8c42', '&:hover': { backgroundColor: '#e67a35' } }}
          >
            Szerepkör hozzáadása
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Felhasználó</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Szerepkör</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Szerepkör leírása</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Műveletek</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userRoles.map((userRole, index) => (
              <TableRow key={`${userRole.userId}-${userRole.roleId}-${index}`} hover>
                <TableCell sx={{ fontWeight: 'medium' }}>
                  {userRole.userName}
                </TableCell>
                <TableCell>
                  {userRole.userEmail || '-'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={userRole.roleName}
                    color="primary"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {userRole.roleDescription || '-'}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {canDelete && (
                    <Tooltip title="Szerepkör eltávolítása">
                      <IconButton
                        size="small"
                        sx={{ color: '#f44336' }}
                        onClick={() => handleDeleteClick(userRole)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {userRoles.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="textSecondary">
            Nincsenek felhasználói szerepek
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Szerepkör eltávolítása</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Biztosan el szeretnéd távolítani a(z) <strong>{selectedUserRole?.roleName}</strong> szerepkört a(z) <strong>{selectedUserRole?.userName}</strong> felhasználótól?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Mégse
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Eltávolítás
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserRolesTable;
